/**
 * Theme Management Utility
 * ========================
 * Handles theme switching and persistence
 */

export type ThemePreset = 'compact' | 'default' | 'spacious' | 'custom';

export interface ThemeConfig {
	preset: ThemePreset;
	densitySpacing?: number;
	densityTypography?: number;
	densityComponents?: number;
	customValues?: Record<string, string>;
}

const THEME_STORAGE_KEY = 'heyweek-theme';
const DEFAULT_THEME: ThemeConfig = {
	preset: 'compact' // Default to compact (Linear-style)
};

/**
 * Get the current theme configuration
 */
export function getCurrentTheme(): ThemeConfig {
	if (typeof window === 'undefined') return DEFAULT_THEME;
	
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch (e) {
		console.warn('Failed to load theme:', e);
	}
	
	return DEFAULT_THEME;
}

/**
 * Apply a theme preset
 */
export function applyTheme(config: ThemeConfig): void {
	if (typeof window === 'undefined') return;
	
	const root = document.documentElement;
	
	// Remove existing theme classes
	root.classList.remove('theme-compact', 'theme-default', 'theme-spacious', 'theme-custom');
	
	// Add new theme class
	root.classList.add(`theme-${config.preset}`);
	
	// Apply preset-specific variables
	switch (config.preset) {
		case 'compact':
			setThemeVariables({
				'--theme-density-spacing': '0.75',
				'--theme-density-typography': '0.875',
				'--theme-density-components': '0.825'
			});
			break;
		
		case 'spacious':
			setThemeVariables({
				'--theme-density-spacing': '1.25',
				'--theme-density-typography': '1.1',
				'--theme-density-components': '1.15'
			});
			break;
		
		case 'default':
			setThemeVariables({
				'--theme-density-spacing': '1',
				'--theme-density-typography': '1',
				'--theme-density-components': '1'
			});
			break;
		
		case 'custom':
			// Apply custom density values if provided
			if (config.densitySpacing !== undefined) {
				root.style.setProperty('--theme-density-spacing', config.densitySpacing.toString());
			}
			if (config.densityTypography !== undefined) {
				root.style.setProperty('--theme-density-typography', config.densityTypography.toString());
			}
			if (config.densityComponents !== undefined) {
				root.style.setProperty('--theme-density-components', config.densityComponents.toString());
			}
			// Apply any additional custom values
			if (config.customValues) {
				Object.entries(config.customValues).forEach(([key, value]) => {
					root.style.setProperty(key, value);
				});
			}
			break;
	}
	
	// Save to localStorage
	try {
		localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(config));
	} catch (e) {
		console.warn('Failed to save theme:', e);
	}
	
	// Dispatch event for components to react
	window.dispatchEvent(new CustomEvent('theme-changed', { detail: config }));
}

/**
 * Set multiple theme variables at once
 */
function setThemeVariables(variables: Record<string, string>): void {
	const root = document.documentElement;
	Object.entries(variables).forEach(([key, value]) => {
		root.style.setProperty(key, value);
	});
}

/**
 * Initialize theme on app load
 */
export function initTheme(): void {
	if (typeof window === 'undefined') return;
	
	const theme = getCurrentTheme();
	applyTheme(theme);
}

/**
 * Create a custom theme with specific density values
 */
export function createCustomTheme(
	spacing: number = 1,
	typography: number = 1,
	components: number = 1
): ThemeConfig {
	return {
		preset: 'custom',
		densitySpacing: spacing,
		densityTypography: typography,
		densityComponents: components
	};
}

/**
 * Quick theme presets for easy switching
 */
export const themes = {
	compact: (): ThemeConfig => ({ preset: 'compact' }),
	default: (): ThemeConfig => ({ preset: 'default' }),
	spacious: (): ThemeConfig => ({ preset: 'spacious' }),
	custom: createCustomTheme
};

/**
 * Reactive theme store for Svelte components
 */
export function createThemeStore() {
	let subscribers: ((theme: ThemeConfig) => void)[] = [];
	let currentTheme = getCurrentTheme();
	
	const subscribe = (callback: (theme: ThemeConfig) => void) => {
		subscribers.push(callback);
		callback(currentTheme);
		
		return () => {
			subscribers = subscribers.filter(cb => cb !== callback);
		};
	};
	
	const set = (theme: ThemeConfig) => {
		currentTheme = theme;
		applyTheme(theme);
		subscribers.forEach(callback => callback(theme));
	};
	
	// Listen for theme changes
	if (typeof window !== 'undefined') {
		window.addEventListener('theme-changed', (e: CustomEvent<ThemeConfig>) => {
			currentTheme = e.detail;
			subscribers.forEach(callback => callback(currentTheme));
		});
	}
	
	return {
		subscribe,
		set,
		update: (updater: (theme: ThemeConfig) => ThemeConfig) => {
			set(updater(currentTheme));
		}
	};
}